import React, { useState, useRef, useEffect, FC, useCallback } from 'react';
import { Button } from '@components-library/Button';
import { IFormField } from '@components-shared/forms/formField.interface';
import ModalPortal from '@components-shared/modal/ModalPortal';
import { useModalEventObserver } from '@components-shared/modal/modalEventObserver';
import { KeyMapKeys, keyMap } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import FormGenerator from 'components/shared/forms/formGenerator';
import i18next from 'i18next';
import { CloseButton } from '../shared/CloseButton';

const el = document.createElement('div');
const { body } = document;

body.appendChild(el);

const FIELDS: IFormField[] = [
  {
    name: 'commentText',
    title: keyMap.sharedFieldsInternalComment,
    type: 'textarea',
    attributes: { autoComplete: 'off' },
    required: true,
  },
];

export type ConfirmModalProps = {
  className?: string;
  title?: string;
  subtitle?: string;
};

export type ConfirmModalState = {
  content: string | React.ReactElement;
  action: null | ((commentText?: string) => Promise<void>) | ((commentText?: string) => void);
  closeAction?: null | (() => void);
  needComment?: boolean;
  confirmBtnText?: string;
  cancelBtnText?: string;
};

interface StaticConfirmModalProps {
  show: (
    content: ConfirmModalState['content'],
    action: ConfirmModalState['action'],
    closeAction?: ConfirmModalState['closeAction'],
    needComment?: ConfirmModalState['needComment'],
    confirmBtnText?: ConfirmModalState['confirmBtnText'],
    cancelBtnText?: ConfirmModalState['cancelBtnText'],
  ) => void;
}

const Form = FormGenerator(FIELDS);

const ConfirmModal: FC<ConfirmModalProps> & StaticConfirmModalProps = ({ className, title, subtitle }) => {
  const [state, setState] = useState<ConfirmModalState>({
    content: '',
    action: null,
    closeAction: null,
    needComment: false,
    confirmBtnText: '',
    cancelBtnText: '',
  });

  const [value, setValue] = useState({ commentText: '' });

  const formRef = useRef<typeof Form>(null);

  const close = useCallback(() => {
    setState({ ...state, content: '', action: null, closeAction: null });
    setValue({ commentText: '' });
  }, [state]);

  useModalEventObserver(close, { condition: Boolean(state.content), ignoreEscButton: true });

  const show = useCallback<StaticConfirmModalProps['show']>(
    (
      content: ConfirmModalState['content'],
      action: ConfirmModalState['action'],
      closeAction: ConfirmModalState['closeAction'] = null,
      needComment = false,
      confirmBtnText: ConfirmModalState['confirmBtnText'],
      cancelBtnText: ConfirmModalState['cancelBtnText'],
    ) => {
      setState({ ...state, content, action, closeAction, needComment, confirmBtnText, cancelBtnText });
    },
    [state],
  );

  useEffect(() => {
    ConfirmModal.show = show;
  }, [show]);

  const action = async () => {
    const { needComment, action } = state;

    if (needComment && formRef.current && !formRef.current.validate()) {
      return;
    }

    if (action) {
      await action(value.commentText);
    }
    close();
  };

  const closeAction = () => {
    close();
    if (state.closeAction) {
      state.closeAction();
    }
  };

  if (!state.content) {
    return null;
  }

  return (
    <ModalPortal>
      <div className="modal-overlay">
        <div className={cn('modal', className)}>
          <div className="modal__container">
            <div className="modal__header">
              {title && <div className="modal__title">{i18next.t(title as KeyMapKeys)}</div>}
              {subtitle && <div className="modal__subtitle">{i18next.t(subtitle as KeyMapKeys)}</div>}
              <CloseButton className="modal__close" onClick={closeAction} />
            </div>
            <div className="modal__body">
              <div className="modal__text">
                {typeof state.content === 'string' ? i18next.t(state.content as KeyMapKeys) : state.content}
              </div>
              {state.needComment && (
                <Form
                  className="admin--form column--form modal__form"
                  form={value}
                  onChange={(_name: never, value: string) => setValue({ commentText: value })}
                  ref={formRef}
                />
              )}
            </div>
            <div className="modal__footer">
              <Button className="modal__btn modal__btn--yes" onClick={action}>
                {i18next.t((state.confirmBtnText as KeyMapKeys) || (keyMap.modalYes as KeyMapKeys))}
              </Button>
              <Button className="modal__btn modal__btn--no" onClick={closeAction}>
                {i18next.t((state.cancelBtnText as KeyMapKeys) || (keyMap.modalNo as KeyMapKeys))}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

ConfirmModal.show = () => console.error('method show is not defined');

export { ConfirmModal };
